/**
 * Redux reducer for admin state, managing companies, roles, groups, employees, and notifications.
 *
 * @module redux/admin/reducer
 * @author Snowfly
 * @version 1.0.0
 */
import {
    DELETE_COMPANY_ROLES_FAIL,
    DELETE_COMPANY_ROLES_REQUEST,
    DELETE_COMPANY_ROLES_SUCCESS,
    FETCH_COMPANY_FAIL,
    FETCH_COMPANY_REQUEST,
    FETCH_COMPANY_ROLES_FAIL,
    FETCH_COMPANY_ROLES_REQUEST,
    FETCH_COMPANY_ROLES_SUCCESS,
    FETCH_COMPANY_SUCCESS,
    FETCH_REQUEST,
    FETCH_FAILURE,
    FETCH_STATUS,
    FETCH_SUB_STATUS,
    FETCH_ROLES,
    FETCH_GROUPS,
    SAVE_USER,
    SAVE_GROUP,
    FETCH_EMPLOYEES,
    ADD_ROLES,
    EDIT_GROUP_IN_USER,
    RESET,
    SAVE_TOOLTIP,
    FETCH_NOTIFICATION,
    FETCH_NOTIFICATIONS,
    RESET_NOTIFICATION, FETCH_COMPANIES,
} from './adminTypes';

/**
 * Initial state for the admin reducer.
 *
 * @type {Object}
 * @property {Array} companyDetails - Details of the currently selected company.
 * @property {Array} rolesCompany - List of roles for the selected company.
 * @property {boolean} isLoading - Whether an async operation is in progress.
 * @property {Array} data - Generic data payload from the last fetch.
 * @property {string|null} status - Last HTTP status or error message.
 * @property {Object} userData - Form data for creating or editing a user.
 * @property {Object} selectStatus - Available status options for select inputs.
 * @property {Object} selectSubStatus - Available sub-status options for select inputs.
 * @property {Array} selectRoles - Available roles for select inputs.
 * @property {Array} selectGroups - Available groups for select inputs.
 * @property {Array} notifications - List of user notifications.
 * @property {number} newNotifications - Count of unread notifications.
 * @property {Object} roles - Current role form data.
 * @property {Object} group - Current group form data.
 * @property {Array} companies - List of all companies.
 * @property {number} totalCompanies - Total count of companies for pagination.
 */
const initialState = {
    companyDetails: [],
    rolesCompany: [],
    isLoading: false,
    data: [],
    status: null,
    userData: {
        first_name: '',
        last_name: '',
        title: '',
        email_id: '',
        phone_number: '',
        password: '',
        status: '',
        sub_status: '',
        role: '',
        schedule: '',
        duration: '',
        employment: '',
        workplace: '',
        joined_company: '',
        probation: '',
        promoted: '',
        groups: [],
        outsourcer: '',
    },
    selectStatus: {},
    selectSubStatus: {},
    selectRoles: [],
    selectGroups: [],
    notifications: [], //{},
    newNotifications: 0,
    roles: {
        name: '',
        company: '',
    },
    group: {
        name: '',
        description: '',
        owner_id: '',
    },
    companies: [],
    totalCompanies: 0,
};

/**
 * Admin reducer function.
 *
 * @param {Object} state - Current admin state.
 * @param {Object} action - Dispatched Redux action.
 * @returns {Object} The next admin state.
 */
const reducer = (state = initialState, action = {}) => {
    const {payload, type} = action;

    switch (type) {
        case FETCH_COMPANY_REQUEST:
            return {
                ...state,
                isLoading: true,
            };

        case FETCH_COMPANY_SUCCESS:
            return {
                ...state,
                companyDetails: payload,
                isLoading: false,
            };

        case FETCH_COMPANY_FAIL:
            return {
                ...state,
                isLoading: false,
            };

        case FETCH_COMPANY_ROLES_REQUEST:
            return {
                ...state,
                isLoading: true,
            };

        case FETCH_COMPANY_ROLES_SUCCESS:
            return {
                ...state,
                rolesCompany: payload,
                isLoading: false,
            };

        case FETCH_COMPANY_ROLES_FAIL:
            return {
                ...state,
                isLoading: false,
            };

        case DELETE_COMPANY_ROLES_REQUEST:
            return {
                ...state,
                isLoading: true,
            };

        case DELETE_COMPANY_ROLES_SUCCESS:
            return {
                ...state,
                rolesCompany: payload,
                isLoading: false,
            };

        case DELETE_COMPANY_ROLES_FAIL:
            return {
                ...state,
                isLoading: false,
            };

        case FETCH_REQUEST:
            return {
                ...state,
                isLoading: true,
            };

        case FETCH_FAILURE:
            return (() => {
                const status = payload.message ? payload.message.split(' ') : [];
                if (status[status.length - 1] === '403') {
                    localStorage.setItem('redirectUrl', '/permissiondenied');
                    window.location.href = '/permissiondenied';
                    return {
                        ...state,
                        isLoading: false,
                        permissionDenied: true,
                    };
                }
                if (status[status.length - 1] === '401') {
                    window.location.href = '/login';
                    localStorage.clear('token');
                    localStorage.clear('user');
                    return {
                        ...state,
                        isLoading: false,
                        isAuth: false,
                        status: action.payload,
                    };
                }
                return {
                    ...state,
                    isLoading: false,
                    status: action.payload,
                };
            })();

        case FETCH_STATUS:
            return {
                ...state,
                isLoading: true,
                selectStatus: action.payload.data,
                status: action.payload.status,
            };

        case FETCH_SUB_STATUS:
            return {
                ...state,
                isLoading: true,
                selectSubStatus: action.payload.data,
                status: action.payload.status,
            };

        case FETCH_ROLES:
            return {
                ...state,
                isLoading: true,
                selectRoles: action.payload.data,
                status: action.payload.status,
            };

        case FETCH_GROUPS:
            return {
                ...state,
                isLoading: true,
                selectGroups: action.payload.data,
                status: action.payload.status,
            }

        case SAVE_USER:
            return {
                ...state,
                result: action.result,
            };

        case ADD_ROLES:
            return {
                ...state,
                roles: action.payload,
                selectRoles: [],
            };

        case SAVE_GROUP:
            return {
                ...state,
                group: action.result,
            };

        case FETCH_EMPLOYEES:
            return {
                ...state,
                employees: action.payload.data,
            };

        case EDIT_GROUP_IN_USER:
            return {
                ...state,
                userGroup: action.payload.data,
            };

        case RESET:
            return {
                ...state,
                rolesCompany: [],
            };

        case SAVE_TOOLTIP:
            return {
                ...state,
                tooltip: action.result,
            };

        case FETCH_NOTIFICATION:
        case FETCH_NOTIFICATIONS:
            return (() => {
                let newIds = action.payload.data.notifList.filter((item) => item.isNew);

                return {
                    ...state,
                    isLoading: true,
                    notifications: action.payload.data.notifList,
                    newNotifications: newIds.length,
                };
            })();

        case RESET_NOTIFICATION:
            return {
                ...state,
                newNotifications: 0,
            };

        case FETCH_COMPANIES:
            return {
                ...state,
                companies: action.payload.data[0].data,
                totalCompanies: action.payload.data[0].metadata[0].total,
            };

        default:
            return state;
    }
};

export default reducer;
